/* Item-specific request capture. This is NOT a fulfillment hold or supplier handoff. */
(function () {
  'use strict';
  const OPEN = '[FOR ANIMALS WITH LOVE PERSONALIZATION]';
  const CLOSE = '[/FOR ANIMALS WITH LOVE PERSONALIZATION]';
  function pieces(config, options) {
    if (!config) return [];
    if (config.type === 'name') return String(options[config.optionIndex] || '').toLowerCase() === 'yes' ? ['Item'] : [];
    const choice = String(options[0] || '').toLowerCase();
    return ['Collar', 'Harness'].filter(piece => choice.includes(piece.toLowerCase()));
  }
  function keysFor(selected) {
    return selected.flatMap(piece => piece === 'Item' ? ['Pet name'] : [1, 2, 3].map(n => `${piece} engraving line ${n}`));
  }
  function problems(selected, properties, quantity) {
    if (!selected.length) return [];
    const errors = [];
    for (const piece of selected) {
      const key = piece === 'Item' ? 'Pet name' : `${piece} engraving line 1`;
      if (!String(properties[key] || '').trim()) errors.push(`Enter ${key.toLowerCase()}.`);
    }
    if (properties['Personalization details checked'] !== 'Yes') errors.push('Check your personalization details.');
    if (properties['Manual verification acknowledged'] !== 'Yes') errors.push('Acknowledge the manual verification requirement.');
    if (properties._fawl_personalization !== 'v2') errors.push('Return to the product page to add this item with its personalization.');
    if (Number(quantity) !== 1) errors.push('Use quantity 1 for each personalized item. Add each additional item separately.');
    return errors;
  }
  function customerNote(note) {
    const value = String(note || '');
    const a = value.indexOf(OPEN), b = value.indexOf(CLOSE, a);
    if (a < 0 || b < a) return value;
    const start = a >= 2 && value.slice(a - 2, a) === '\n\n' ? a - 2 : a;
    return value.slice(0, start) + value.slice(b + CLOSE.length);
  }
  function itemPieces(item, products) {
    return pieces(products[String(item.product_id)], item.options_with_values?.map(o => o.value) || item.variant_options || []);
  }
  function noteBlock(cart, products) {
    const items = (cart.items || []).filter(i => itemPieces(i, products).length && i.properties?._fawl_personalization === 'v2');
    if (!items.length) return '';
    return OPEN + '\nMANUAL VERIFICATION REQUIRED\nThis note is not a fulfillment hold. Confirm instructions with the customer and supplier before production.\n\n' +
      items.map((item, index) => [
        `Item ${index + 1}: ${item.product_title || item.title}`,
        `Cart line reference: ${item.key}`, `Selected variant: ${item.variant_title}`,
        `SKU: ${item.sku || 'Not provided'}`, `Quantity: ${item.quantity}`,
        ...keysFor(itemPieces(item, products)).map(key => `${key}: ${item.properties[key] || 'Not requested'}`),
        'Personalization details checked: Yes', 'Manual verification acknowledged: Yes'
      ].join('\n')).join('\n\n') + '\n\n' + CLOSE;
  }
  function note(cart, products, custom) {
    const free = custom === undefined ? customerNote(cart.note) : custom;
    const generated = noteBlock(cart, products);
    return free + (free && generated ? '\n\n' : '') + generated;
  }
  function cartProblems(cart, products) {
    return (cart.items || []).flatMap(item => problems(itemPieces(item, products), item.properties || {}, item.quantity));
  }
  const core = {pieces, keysFor, problems, customerNote, itemPieces, noteBlock, note, cartProblems};
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (typeof document === 'undefined') return;
  const configElement = document.getElementById('fawl-personalization-config');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  if (!config.enabled) return;
  const products = config.products;
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  let state = null, queue = Promise.resolve(), saveError = '', dirtyNote, checkoutBusy = false;
  async function readCart() {
    const response = await nativeFetch(root + 'cart.js', {credentials:'same-origin', cache:'no-store'});
    if (!response.ok) throw new Error('Cart read failed');
    return response.json();
  }
  function render() {
    const relevant = Boolean(state?.items?.some(item => itemPieces(item, products).length));
    document.body.classList.toggle('fawl-personalized-cart', relevant);
    document.querySelectorAll('fawl-personalization-note').forEach(panel => {
      panel.hidden = !relevant && !saveError;
      const input = panel.querySelector('[data-fawl-customer-note]');
      if (input && document.activeElement !== input) input.value = dirtyNote ?? customerNote(state?.note);
      panel.querySelector('[data-fawl-note-review]').textContent = state ? noteBlock(state, products) : '';
      const status = panel.querySelector('[data-fawl-note-status]');
      status.dataset.error = String(Boolean(saveError));
      status.textContent = saveError || (dirtyNote !== undefined ? 'You have unsaved order notes.' : 'Personalization requests saved. Manual verification is required before production.');
    });
  }
  function sync(custom) {
    const task = async () => {
      const cart = await readCart(), wanted = note(cart, products, custom);
      if ((cart.note || '') !== wanted) {
        const response = await nativeFetch(root + 'cart/update.js', {method:'POST', credentials:'same-origin', headers:{'Content-Type':'application/json'}, body:JSON.stringify({note:wanted})});
        if (!response.ok) throw new Error('Note update failed');
      }
      state = await readCart();
      if ((state.note || '') !== note(state, products, custom)) throw new Error('Note verification failed');
      if (custom !== undefined && dirtyNote === custom) dirtyNote = undefined;
      saveError = ''; render(); return state;
    };
    const result = queue.catch(() => {}).then(task).catch(error => {
      saveError = 'Your personalization notes could not be verified. Open your cart and save again before checkout.';
      render(); throw error;
    });
    queue = result;
    return result;
  }
  window.fetch = async function(input, options) {
    const response = await nativeFetch(input, options);
    let mutation = false;
    try {
      const url = new URL(typeof input === 'string' ? input : input?.url || String(input), location.href);
      mutation = url.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
    } catch (_) {}
    if (mutation && response.ok) { try { await sync(); } catch (_) {} }
    return response;
  };
  class Personalization extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-fawl-variants]').textContent);
      this.timer = setInterval(() => this.refresh(), 250);
      this.refresh();
    }
    disconnectedCallback() { clearInterval(this.timer); }
    refresh() {
      const form = this.closest('form');
      const variant = this.variants.find(v => String(v.id) === form?.querySelector('[name=id]')?.value);
      this.selected = variant ? pieces(products[this.dataset.productId], variant.options) : [];
      this.querySelectorAll('[data-fawl-piece]').forEach(fieldset => {
        fieldset.hidden = fieldset.disabled = !this.selected.includes(fieldset.dataset.fawlPiece);
      });
      const common = this.querySelector('[data-fawl-common]');
      common.hidden = common.disabled = !this.selected.length;
      this.hidden = !this.selected.length;
    }
    validate() {
      this.refresh();
      const form = this.closest('form');
      const requestId = this.querySelector('[name="properties[_fawl_request_id]"]');
      if (this.selected.length) requestId.value = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const data = new FormData(form);
      const properties = Object.fromEntries([...data].filter(([key]) => key.startsWith('properties[')).map(([key,value]) => [key.slice(11,-1), value]));
      const errors = problems(this.selected, properties, data.get('quantity') || 1);
      const panel = this.querySelector('[data-fawl-error]');
      panel.textContent = errors.join(' ');
      if (errors.length) { panel.focus(); this.scrollIntoView({block:'center'}); }
      return !errors.length;
    }
  }
  if (!customElements.get('fawl-personalization')) customElements.define('fawl-personalization', Personalization);
  if (!customElements.get('fawl-personalization-note')) customElements.define('fawl-personalization-note', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-fawl-customer-note]').addEventListener('input', event => { dirtyNote = event.target.value; render(); });
      this.querySelector('[data-fawl-save-note]').addEventListener('click', () => sync(this.querySelector('[data-fawl-customer-note]').value).catch(() => {}));
      render();
    }
  });
  async function checkout(event) {
    if (state && !state.items?.some(item => itemPieces(item, products).length) && !saveError) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (checkoutBusy) return;
    checkoutBusy = true;
    try {
      const cart = await sync(dirtyNote), errors = cartProblems(cart, products);
      if (errors.length) {
        saveError = errors.join(' '); render();
        if (!document.querySelector('fawl-personalization-note:not([hidden])')) location.assign(root + 'cart');
        else document.querySelector('fawl-personalization-note:not([hidden])').scrollIntoView({block:'center'});
      } else location.assign(root + 'checkout');
    } catch (_) {
      if (!document.querySelector('fawl-personalization-note:not([hidden])')) location.assign(root + 'cart');
    } finally { checkoutBusy = false; }
  }
  window.addEventListener('submit', event => {
    const component = event.target.querySelector('fawl-personalization');
    if (component && !component.validate()) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (event.submitter?.name === 'checkout') checkout(event);
  }, true);
  window.addEventListener('click', event => {
    const trigger = event.target.closest('button[name=checkout],input[name=checkout],a[href]');
    if (!trigger) return;
    let checkoutLink = trigger.name === 'checkout';
    if (trigger.tagName === 'A') {
      try { const url = new URL(trigger.href); checkoutLink = url.origin === location.origin && /\/checkout\/?$/.test(url.pathname); } catch (_) {}
    }
    if (checkoutLink) checkout(event);
  }, true);
  window.FAWLPersonalization = {...core, sync, products, getState: () => state};
  sync().catch(() => {});
})();
